/**
 * Keeps contextual knowledge reads on the work item's current resolution
 * (ADR 0091 D3; docs/plans/2026-10-20-feat-knowledge-spaces-frontend-context-scoping-plan.md
 * U3, U4). A read whose key names an obsolete tuple never starts and never
 * delivers (`isCurrentContextKey`, checked by the read itself before its
 * request and after its await); when a resolution changes, the entries of every
 * other tuple of that work item are cancelled and removed. Every decision here
 * is synchronous. Cache invalidation supplements the server's checks; it never
 * supplies authority.
 *
 * Also holds the mismatch stopping policy: one automatic same-tuple retry after
 * the server did not confirm a key's tuple, then stopped until the user retries.
 * The state lives outside the query, so it survives observer removal and
 * garbage collection.
 */
import { hashKey } from '@tanstack/react-query';

import { KNOWLEDGE_QUERY_KEY } from '@/hooks/pii-query-key-roots';
import { knowledgeKeys } from '@/hooks/use-knowledge-keys';

import {
  UNRESOLVED_CONTEXT_TUPLE,
  contextTuple,
  parseWorkItemKey,
  readContextSegment,
  workItemKey,
  type KnowledgeContextScope,
} from './knowledge-query-scope';

import type { Query, QueryClient } from '@tanstack/react-query';
import type {
  KnowledgeWorkContextResolution,
  KnowledgeWorkItem,
} from '@/hooks/use-knowledge-work-context';

export interface MismatchState {
  attempts: number;
  stopped: boolean;
}

const WORK_CONTEXT_SUBKEY = knowledgeKeys.workContexts()[1];

const mismatchStates = new WeakMap<QueryClient, Map<string, MismatchState>>();
const mismatchListeners = new WeakMap<QueryClient, Set<() => void>>();

function statesOf(client: QueryClient): Map<string, MismatchState> {
  let states = mismatchStates.get(client);
  if (!states) {
    states = new Map();
    mismatchStates.set(client, states);
  }
  return states;
}

function notifyMismatch(client: QueryClient): void {
  for (const listener of mismatchListeners.get(client) ?? []) {
    listener();
  }
}

function writeMismatch(client: QueryClient, hash: string, state: MismatchState | null): void {
  const states = statesOf(client);
  if (state === null) {
    if (!states.delete(hash)) {
      return;
    }
  } else {
    states.set(hash, state);
  }
  notifyMismatch(client);
}

function readMismatch(client: QueryClient, hash: string): MismatchState {
  return mismatchStates.get(client)?.get(hash) ?? { attempts: 0, stopped: false };
}

/** For `useSyncExternalStore`: a reader re-renders when any key's stop state changes. */
export function subscribeToContextMismatch(client: QueryClient, listener: () => void): () => void {
  let listeners = mismatchListeners.get(client);
  if (!listeners) {
    listeners = new Set();
    mismatchListeners.set(client, listeners);
  }
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function keyText(value: unknown): string {
  return typeof value === 'string' ? value : '';
}

function keyActor(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function isWorkContextKey(key: readonly unknown[]): boolean {
  return key[0] === KNOWLEDGE_QUERY_KEY && key[1] === WORK_CONTEXT_SUBKEY && key.length === 6;
}

function resolvedTuple(query: Query | undefined): string | null {
  if (query?.state.status !== 'success') {
    return null;
  }
  const data = query.state.data as KnowledgeWorkContextResolution | undefined;
  if (data?.state !== 'resolved') {
    return null;
  }
  return contextTuple(data.engagementId, data.revision);
}

/**
 * True only when the work-context query the key names (organisation at index
 * 2, actor at 3) is a success, is resolved, and has the key's engagement and
 * revision. Not true for an office key, an unresolved segment, or a read whose
 * resolution has since failed.
 */
export function isCurrentContextKey(client: QueryClient, queryKey: readonly unknown[]): boolean {
  const segment = readContextSegment(queryKey);
  if (segment === null || segment.tuple === UNRESOLVED_CONTEXT_TUPLE) {
    return false;
  }
  const item = parseWorkItemKey(segment.workItemKey);
  if (item === null) {
    return false;
  }
  const workContext = client.getQueryCache().find({
    queryKey: knowledgeKeys.workContext(
      keyText(queryKey[2]),
      keyActor(queryKey[3]),
      item.kind,
      item.id,
    ),
    exact: true,
  });
  return resolvedTuple(workContext) === segment.tuple;
}

export function isContextMismatchStopped(
  client: QueryClient,
  queryKey: readonly unknown[],
): boolean {
  return readMismatch(client, hashKey(queryKey)).stopped;
}

/** Back to `{ attempts: 0, stopped: false }`: after a success, an eviction or a tuple change. */
export function clearContextMismatch(client: QueryClient, queryKey: readonly unknown[]): void {
  writeMismatch(client, hashKey(queryKey), null);
}

/**
 * Re-reads the work item's recorded context (a generation or a search may have
 * just recorded a derived answer), refetching the exact key whether or not
 * anything observes it.
 */
export async function adoptRecordedWorkContext(
  client: QueryClient,
  input: { organizationId: string; actorId: string | null; workItem: KnowledgeWorkItem },
): Promise<void> {
  await client.invalidateQueries({
    queryKey: knowledgeKeys.workContext(
      input.organizationId,
      input.actorId,
      input.workItem.kind,
      input.workItem.id,
    ),
    exact: true,
    refetchType: 'all',
  });
}

/**
 * Called, not awaited, by a contextual read whose tuple the server did not
 * confirm. After adoption: a tuple that moved is the new key's business (fresh
 * state); the same tuple gets one exact refetch, and a second mismatch stops
 * the key until `retryContextMismatch`.
 */
export async function recoverContextMismatch(
  client: QueryClient,
  queryKey: readonly unknown[],
): Promise<void> {
  const segment = readContextSegment(queryKey);
  const item = segment ? parseWorkItemKey(segment.workItemKey) : null;
  if (segment === null || item === null || segment.tuple === UNRESOLVED_CONTEXT_TUPLE) {
    return;
  }
  await adoptRecordedWorkContext(client, {
    organizationId: keyText(queryKey[2]),
    actorId: keyActor(queryKey[3]),
    workItem: item,
  });
  if (!isCurrentContextKey(client, queryKey)) {
    return;
  }
  const hash = hashKey(queryKey);
  const state = readMismatch(client, hash);
  if (state.attempts === 0) {
    writeMismatch(client, hash, { attempts: 1, stopped: false });
    await client.refetchQueries({ queryKey, exact: true });
    return;
  }
  writeMismatch(client, hash, { attempts: state.attempts, stopped: true });
}

/** The user's retry: unstopped with its automatic attempt spent, then one exact refetch. */
export async function retryContextMismatch(
  client: QueryClient,
  queryKey: readonly unknown[],
): Promise<void> {
  writeMismatch(client, hashKey(queryKey), { attempts: 1, stopped: false });
  await client.refetchQueries({ queryKey, exact: true });
}

interface EvictionTarget {
  actorId: string;
  workItemKey: string;
  /** The tuple to keep; null keeps none (the work item no longer resolves). */
  current: string | null;
}

function isObsoleteEntry(query: Query, target: EvictionTarget): boolean {
  if (query.queryKey[3] !== target.actorId) {
    return false;
  }
  const segment = readContextSegment(query.queryKey);
  return (
    segment !== null &&
    segment.workItemKey === target.workItemKey &&
    segment.tuple !== UNRESOLVED_CONTEXT_TUPLE &&
    segment.tuple !== target.current
  );
}

/**
 * Cancels what is in flight, removes what nothing observes, and returns the
 * hashes still observed (removed once their last observer goes).
 */
function evictEntries(
  client: QueryClient,
  target: EvictionTarget,
  isActive: () => boolean = () => true,
): string[] {
  const cache = client.getQueryCache();
  const deferred: string[] = [];
  for (const query of cache.getAll()) {
    if (!isActive()) {
      break;
    }
    if (!isObsoleteEntry(query, target)) {
      continue;
    }
    clearContextMismatch(client, query.queryKey);
    if (query.state.fetchStatus === 'fetching') {
      query.cancel({ revert: true }).catch(() => null);
    }
    if (query.getObserversCount() === 0) {
      cache.remove(query);
    } else {
      deferred.push(query.queryHash);
    }
  }
  return deferred;
}

/**
 * On demand, for a caller that knows the new tuple before any read does (a
 * choice, with the revision `knowledge_work_context_set` returned). An observed
 * entry is cancelled here and left for the subscription to remove on unmount.
 */
export function evictObsoleteWorkContext(
  client: QueryClient,
  input: { actorId: string; workItem: KnowledgeWorkItem; current: KnowledgeContextScope },
): void {
  const { actorId, current, workItem } = input;
  evictEntries(client, {
    actorId,
    workItemKey: workItemKey(workItem),
    current: current.kind === 'work' ? contextTuple(current.engagementId, current.revision) : null,
  });
}

/**
 * Subscribed by `CacheScopeProvider` for the signed-in actor. Acts on a
 * successful work-context read whose tuple differs from the last one seen for
 * that key; the first read of a key is only the baseline.
 */
export function subscribeToWorkContextEviction(
  client: QueryClient,
  actorId: string | null,
): () => void {
  if (actorId === null) {
    return () => {
      /* nothing was subscribed */
    };
  }
  const cache = client.getQueryCache();
  const seen = new Map<string, string | null>();
  const removeOnUnobserved = new Set<string>();
  let active = true;

  for (const query of cache.getAll()) {
    const ownResolution = isWorkContextKey(query.queryKey) && query.queryKey[3] === actorId;
    if (ownResolution && query.state.status === 'success') {
      seen.set(query.queryHash, resolvedTuple(query));
    }
  }

  const onResolution = (query: Query): void => {
    const tuple = resolvedTuple(query);
    const known = seen.has(query.queryHash);
    const previous = seen.get(query.queryHash);
    seen.set(query.queryHash, tuple);
    if (!known || previous === tuple) {
      return;
    }
    const kind = query.queryKey[4] as KnowledgeWorkItem['kind'];
    const id = keyText(query.queryKey[5]);
    const deferred = evictEntries(
      client,
      { actorId, workItemKey: workItemKey({ kind, id }), current: tuple },
      () => active,
    );
    for (const hash of deferred) {
      removeOnUnobserved.add(hash);
    }
  };

  const unsubscribe = cache.subscribe((event) => {
    if (!active) {
      return;
    }
    if (event.type === 'removed') {
      removeOnUnobserved.delete(event.query.queryHash);
      return;
    }
    if (event.type === 'observerRemoved') {
      if (removeOnUnobserved.has(event.query.queryHash) && event.query.getObserversCount() === 0) {
        removeOnUnobserved.delete(event.query.queryHash);
        cache.remove(event.query);
      }
      return;
    }
    if (event.type !== 'updated' || event.action.type !== 'success') {
      return;
    }
    const key = event.query.queryKey as readonly unknown[];
    if (isWorkContextKey(key) && key[3] === actorId) {
      onResolution(event.query as Query);
    }
  });

  return () => {
    active = false;
    unsubscribe();
  };
}
